import { createContext, useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Star, ChevronDown, Landmark, SlidersHorizontal, Users, ClipboardList, ScanLine } from "lucide-react";
import { NAV, canAccess } from "@/lib/access";
import { useApp } from "@/context/AppContext";
import { initials, api } from "@/lib/api";

const BadgeContext = createContext({});

const Badge = ({ n, testId }) => (n > 0 ? <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#E60000] text-white text-[11px] font-bold grid place-items-center" data-testid={testId}>{n > 99 ? "99+" : n}</span> : null);

const useBadges = (user) => {
  const [counts, setCounts] = useState({});
  const { pathname } = useLocation();
  const allowed = canAccess(user, "messages");
  useEffect(() => {
    if (!allowed) { return undefined; }
    const load = () => api.get("/contact-messages/count").then((r) => setCounts({ messages: r.data.open })).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("contact-messages-changed", load);
    return () => { clearInterval(t); window.removeEventListener("contact-messages-changed", load); };
  }, [allowed, pathname]);
  return counts;
};

const Brand = ({ collapsed }) => (
  <Link to="/" title="Open public homepage" className="flex items-center gap-3 px-5 py-5 border-b border-[#222] hover:bg-[#1A1A1A] transition-colors" data-testid="gamls-swappable-logo-slot">
    <div className="shrink-0 w-11 h-11 rounded-full bg-white p-0.5 ring-2 ring-[#E60000]">
      <img src="/images/gamls-logo.png" alt="GAMLS logo" className="w-full h-full object-contain" />
    </div>
    {!collapsed && (
      <div className="leading-tight">
        <div className="font-heading font-extrabold text-white text-lg tracking-tight">GAMLS <span className="text-[#E60000]">EventHub</span></div>
        <div className="text-[10px] uppercase tracking-[0.08em] leading-snug text-white/45 mt-0.5">Ghana Association of Medical Laboratory Scientists</div>
      </div>
    )}
  </Link>
);

const Profile = ({ collapsed }) => {
  const { user, role } = useApp();
  if (!user) return null;
  return (
    <div className={`mx-3 my-4 rounded-lg bg-[#1A1A1A] border border-[#262626] ${collapsed ? "p-2 flex justify-center" : "p-3"}`} data-testid="sidebar-profile-badge">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 shrink-0 rounded-md bg-[#E60000] text-white font-heading font-bold grid place-items-center">{initials(user.name)}</div>
        {!collapsed && (
          <div className="min-w-0">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="font-mono text-[11px] text-white/50">@{user.username}</div>
          </div>
        )}
      </div>
      {!collapsed && (
        <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#E60000]/15 border border-[#E60000]/40 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-[#FF4D4D]" data-testid="sidebar-active-role-tag">
          <span className="w-1.5 h-1.5 rounded-full bg-[#E60000] animate-pulse" />{role}
        </div>
      )}
    </div>
  );
};

const NavItem = ({ item, collapsed, onNavigate }) => {
  const n = useContext(BadgeContext)[item.id] || 0;
  return (
  <NavLink
    to={item.path}
    onClick={onNavigate}
    title={item.label}
    data-testid={`nav-tab-${item.id}`}
    className={({ isActive }) =>
      `group relative flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${
        isActive ? "bg-[#262626] text-white" : "text-white/60 hover:bg-[#1C1C1C] hover:text-white"
      } ${collapsed ? "justify-center" : ""}`
    }
  >
    {({ isActive }) => (
      <>
        <span className={`absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[#E60000] transition-opacity ${isActive ? "opacity-100" : "opacity-0"}`} />
        <item.icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? "text-[#E60000]" : ""}`} strokeWidth={1.8} />
        {!collapsed && <span className="flex-1 leading-snug">{item.label}</span>}
        {!collapsed && <Badge n={n} testId={`nav-badge-${item.id}`} />}
        {collapsed && n > 0 && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#E60000]" />}
      </>
    )}
  </NavLink>
  );
};

const GROUP_ICONS = { Accounts: Landmark, Control: SlidersHorizontal, Membership: Users, "Event Registration": ClipboardList, "Gate Check-In": ScanLine };

const NavGroup = ({ name, items, collapsed, onNavigate, open, onToggle }) => {
  const Icon = GROUP_ICONS[name] || Landmark;
  const { pathname } = useLocation();
  const active = items.some((i) => pathname.startsWith(i.path));
  const slug = name.toLowerCase().replace(/\s+/g, "-");
  const badges = useContext(BadgeContext);
  const total = items.reduce((s, i) => s + (badges[i.id] || 0), 0);
  return (
    <div data-testid={`nav-group-${slug}`}>
      <button onClick={onToggle} title={name} className={`w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors duration-200 ${active ? "text-white" : "text-white/60 hover:bg-[#1C1C1C] hover:text-white"} ${collapsed ? "justify-center" : ""}`} aria-expanded={open} data-testid={`nav-group-toggle-${slug}`}>
        <Icon className={`w-[18px] h-[18px] shrink-0 ${active ? "text-[#E60000]" : ""}`} strokeWidth={1.8} />
        {!collapsed && <><span className="flex-1 text-left leading-snug font-semibold">{name}</span>{!open && <Badge n={total} testId={`nav-group-badge-${slug}`} />}<ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`} /></>}
      </button>
      {open && <div className={`space-y-0.5 ${collapsed ? "" : "ml-4 pl-2 border-l border-[#262626]"}`} data-testid={`nav-group-items-${slug}`}>
        {items.map((item) => <NavItem key={item.id} item={item} collapsed={collapsed} onNavigate={onNavigate} />)}
      </div>}
    </div>
  );
};

const navEntries = (items) => items.reduce((out, item) => {
  const g = item.group && out.find((e) => e.group === item.group);
  if (g) g.items.push(item);
  else out.push(item.group ? { group: item.group, items: [item] } : item);
  return out;
}, []);

export const Sidebar = ({ collapsed, mobileOpen, onNavigate }) => {
  const { user } = useApp();
  const [openGroup, setOpenGroup] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => {
    const hit = NAV.find((i) => i.group && (pathname === i.path || pathname.startsWith(`${i.path}/`)));
    if (hit) { setOpenGroup(hit.group); }
  }, [pathname]);
  const badges = useBadges(user);
  return (
    <BadgeContext.Provider value={badges}>
    <aside
      data-testid="gamls-sidebar-container"
      className={`fixed lg:sticky top-0 left-0 z-40 h-screen bg-[#111111] flex flex-col transition-[width,transform] duration-300 ${
        collapsed ? "lg:w-20" : "lg:w-72"
      } w-72 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
    >
      <Brand collapsed={collapsed} />
      <Profile collapsed={collapsed} />
      {!collapsed && <div className="px-6 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/30">Modules</div>}
      <nav className="flex-1 overflow-y-auto px-3 space-y-0.5 sidebar-scroll">
        {navEntries(NAV.filter((item) => canAccess(user, item.id))).map((e) => e.group
          ? <NavGroup key={e.group} name={e.group} items={e.items} collapsed={collapsed} onNavigate={onNavigate} open={openGroup === e.group} onToggle={() => setOpenGroup((g) => (g === e.group ? null : e.group))} />
          : <NavItem key={e.id} item={e} collapsed={collapsed} onNavigate={onNavigate} />)}
      </nav>
      <div className="bg-[#E60000] px-4 py-3.5 flex items-center justify-center gap-2" data-testid="sidebar-tagline-service-to-humanity">
        <Star className="w-3 h-3 fill-white text-white" />
        {!collapsed ? (
          <span className="font-heading font-extrabold text-white text-sm tracking-[0.16em]">SERVICE TO HUMANITY</span>
        ) : (
          <span className="font-heading font-extrabold text-white text-[10px]">STH</span>
        )}
        {!collapsed && <Star className="w-3 h-3 fill-white text-white" />}
      </div>
    </aside>
    </BadgeContext.Provider>
  );
};
